import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import { BlueIcon, CenteredButtonRow, Clickable, Row, RowWithGap, RowWithSpaceBetween } from '@components/styles';
import dayjs, { Dayjs } from 'dayjs';
import {
  apiPragueFromDayjsInTz,
  apiUtcFromDayjs,
  dayjsFromApiUtcInTz,
  findNextDay,
  formatDateFromDayjsWithLocale,
  shortDateFormat,
  stringFromTimeslotResponse,
  stringHHmmFromDayjs,
} from '@utils/dateWithTz';
import {
  CompanyLocationRampWithOpeningHoursOutDTO,
  DayEnum,
  DefaultVersionEnum,
  GlobalOrderItemsRequest,
  GlobalOrderResponse,
  OrderReservationInDTO,
  ReservationActionTypesEnum,
  ReservationDataInDTO,
  ReservationDetailOutDTO,
  ReservationDriverInfoOutDTO,
  ReservationFlagDTO,
} from '@api/ringil-api.v2';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { useImmer } from 'use-immer';
import useNavigation from '@hooks/useNavigation.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import { RingilDialog } from '@components/obsolete/RingilDialog/RingilDialog.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import { useTranslation } from 'react-i18next';
import { useDuplicateDateOptions } from '@components/features/DuplicateDatesButtons/duplicateDateOptions.ts';
import { Link } from 'react-router-dom';
import InputCompanyRamp from '@components/ringil3/Input/InputCompanyRamp/InputCompanyRamp';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion.tsx';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces.ts';
import { toEditableFlags } from '@components/RingilComponents/form/flagUnitsLimit';

interface ComponentProps {
  onComplete: () => void;
  /** Company owning the location — needed to load the ramps of a replacement location. */
  customerId?: string;
  /**
   * Set when the reservation's own location has been superseded (RD-2872): the copies are created on
   * the replacement location instead. The original ramp belongs to the old location, so the user picks
   * one on the replacement before anything can be checked or saved.
   */
  replacementLocation?: { locationId: string; locationName: string };
}

type SlotState = 'check' | 'free' | 'save' | 'error' | 'saved';

interface Slot {
  id: string;
  since: Dayjs;
  till: Dayjs;
  timezoneId: string;
  newReservationId?: string;
  state: SlotState;
}

/**
 * Re-creates a tz-aware dayjs from its wall-clock time so the UTC offset is recomputed.
 * dayjs `add`/`set` on a tz-aware object keep the original offset, which becomes wrong
 * once the result crosses a DST change (isSame with itself fails, UTC instant is 1h off).
 */
const normalizeInTz = (d: Dayjs, timezoneId: string): Dayjs => dayjs.tz(d.format('YYYY-MM-DDTHH:mm:ss'), timezoneId);

/** Keeps a slot's wall-clock time but re-anchors it in another timezone, and marks it for a re-check. */
const retimeSlot = (slot: Slot, timezoneId: string): Slot => ({
  ...slot,
  since: normalizeInTz(slot.since, timezoneId),
  till: normalizeInTz(slot.till, timezoneId),
  timezoneId,
  state: 'check',
});

const newSlotId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface ReservationTemplateInput {
  customerId: string;
  locationId: string;
  rampId: string;
  order: OrderReservationInDTO;
  orders: GlobalOrderResponse[];
  carPlate?: string;
  driverInfo?: ReservationDriverInfoOutDTO;
  carrierNote?: string;
  carrierId?: string;
  reservationName?: string;
  reservationNote?: string;
  reservationActionTypes?: ReservationActionTypesEnum[];
  flags?: ReservationFlagDTO[];
  sendNotification: boolean;
}

/** The reservation body a copy is created from — everything but the timeslot, which differs per slot. */
const buildReservationTemplate = (input: ReservationTemplateInput): Omit<ReservationDataInDTO, 'timeslot'> => ({
  createExpedition: false,
  customerId: input.customerId,
  locationId: input.locationId,
  labelIds: [],
  rampId: input.rampId,
  order: input.order,
  openGate: false,
  carrierNote: input.carrierNote,
  driverInfo: input.driverInfo,
  carPlate: input.carPlate,
  reservationName: input.reservationName,
  reservationNote: input.reservationNote,
  carrierId: input.carrierId,
  reservationActionTypes: input.reservationActionTypes,
  flags: input.flags ?? [],
  sendNotification: input.sendNotification,
  orders: input.orders.map(t => ({
    orderValue: t.orderValue,
    items: t.items.map(
      t =>
        ({
          amount: t.amount,
          stackable: t.stackable,
          note: t.itemNote,
          height: t.height,
          width: t.width,
          length: t.length,
          weight: t.weight,
          cargoTemplateCode: t.cargoTemplate.code,
        }) as GlobalOrderItemsRequest,
    ),
    cargoAmount: t.orderAmount,
    cargoDescription: t.orderDescription,
    orderNote: t.orderNote,
    orderName: t.orderName,
  })),
});

export const useDuplicateTimeslot = () => {
  const [timeslotIdForDuplication, setTimeslotIdForDuplication] = useState(null);

  const DuplicateTimeslot: React.FC<ComponentProps> = ({ onComplete, customerId, replacementLocation }) => {
    const { tr } = useTranslationLgs();
    const { getReservationDetail, createReservationMultiDuplicationJob } = useContext(ApiContext);
    const [newSlots, setNewSlots] = useImmer<Slot[]>([]);
    // NEW = one async job creates the reservations (job detail page shows the progress);
    // ORIGINAL = this dialog creates them itself, one request per slot.
    const { version: asyncJobVersion } = useBetaVersion(BetaSurface.RESERVATION_MULTI_DUPLICATION);
    const asyncJob = asyncJobVersion === DefaultVersionEnum.NEW;

    const [lastSlot, setLastSlot] = useState<Slot>();
    const [reservationData, setReservationData] = useState<ReservationDetailOutDTO>();

    const [saving, setSaving] = useState(false);
    const [sendNotification, setSendNotification] = useState(true);

    // Copies of a reservation on a superseded location are created on its replacement, on a ramp the
    // user picks there — the reservation's own ramp id means nothing on the new location.
    const isReplaced = isNotNullOrUndefined(replacementLocation);
    const [targetRampId, setTargetRampId] = useState<string>(null);
    const [targetRamps, setTargetRamps] = useState<CompanyLocationRampWithOpeningHoursOutDTO[]>([]);
    const waitingForRamp = isReplaced && isNullOrUndefined(targetRampId);

    const navigation = useNavigation();

    useEffect(() => {
      const c = new AbortController();

      if (timeslotIdForDuplication) {
        getReservationDetail(timeslotIdForDuplication, d => {
          setReservationData(d);
          const tz = d.slot?.timezoneId ?? d.timeslot?.timezoneId ?? 'Europe/Prague';
          const utcStart = d.slot?.utcStart ?? d.timeslot?.utcStart ?? d.timeslot?.since;
          const utcEnd   = d.slot?.utcEnd   ?? d.timeslot?.utcEnd   ?? d.timeslot?.till;
          setLastSlot({
            id: newSlotId(),
            since: dayjsFromApiUtcInTz(utcStart, tz),
            till:  dayjsFromApiUtcInTz(utcEnd,   tz),
            timezoneId: tz,
            state: 'check',
          });
        });
      }

      return () => {
        c.abort();
      };
    }, [timeslotIdForDuplication]);

    // A ramp of the replacement location can sit in another timezone than the original one: keep the
    // wall-clock times the user sees and re-anchor them there. Every slot is re-checked against the
    // newly picked ramp.
    useEffect(() => {
      if (!isReplaced || isNullOrUndefined(targetRampId)) return;
      const tz = targetRamps.find(r => r.rampId === targetRampId)?.timezoneId;
      if (isNullOrUndefined(tz)) return;
      setLastSlot(s => (s ? retimeSlot(s, tz) : s));
      // Slots already being saved (or saved) are left alone — only the pending ones move.
      setNewSlots(d => d.map(slot => (slot.state === 'save' || slot.state === 'saved' ? slot : retimeSlot(slot, tz))));
    }, [targetRampId, targetRamps]);

    const createNewSlot = (daysToAdd: number) => {
      const l = newSlots.length > 0 ? newSlots[newSlots.length - 1] : lastSlot;

      const newSlot = {
        id: newSlotId(),
        since: normalizeInTz(l.since.add(daysToAdd, 'day'), l.timezoneId),
        till: normalizeInTz(l.till.add(daysToAdd, 'day'), l.timezoneId),
        timezoneId: l.timezoneId,
        state: 'check',
      } as Slot;

      pushSlot(newSlot);
    };

    const changeSlotState = (slot: Slot) => {
      setNewSlots(draft => {
        const s = draft.find(s => s.id === slot.id);
        if (s) s.state = slot.state;
      });
    };

    /*    const delayedCheck = (slot: Slot) => {
                      changeSlotState({ ...slot, hasPendingApiRequest: true });
                    };*/
    /**
     * Add slot to state and trigger api delayed api check
     * @param slot
     */
    const pushSlot = (slot: Slot) => {
      setNewSlots(d => {
        d.push(slot);
      });
      //delayedCheck(slot);
    };

    const createNewSlotWithDate = (date: Dayjs) => {
      const newSlot = {
        id: newSlotId(),
        since: normalizeInTz(lastSlot.since.set('date', date.date()).set('month', date.month()).set('year', date.year()), lastSlot.timezoneId),
        till: normalizeInTz(lastSlot.till.set('date', date.date()).set('month', date.month()).set('year', date.year()), lastSlot.timezoneId),
        timezoneId: lastSlot.timezoneId,
        state: 'check',
      } as Slot;
      setLastSlot(newSlot);
      pushSlot(newSlot);
    };

    /**
     * Create new slot with next day in week, next Monday, Tuesday, etc.
     * @param dayInWeek
     */
    const createNewSlotNext = (dayInWeek: DayEnum) => {
      const l = newSlots.length > 0 ? newSlots[newSlots.length - 1] : lastSlot;

      const next = findNextDay(l.since, dayInWeek);
      const duration = l.till.diff(l.since, 'minutes');
      const newSince = l.since.set('date', next.date()).set('month', next.month()).set('year', next.year());

      const newSlot = {
        id: newSlotId(),
        since: normalizeInTz(newSince, l.timezoneId),
        till: normalizeInTz(newSince.add(duration, 'minutes'), l.timezoneId),
        timezoneId: l.timezoneId,
        state: 'check',
      } as Slot;
      pushSlot(newSlot);
    };

    const removeSlot = (id: string) => {
      setNewSlots(d => {
        return d.filter(s => s.id !== id);
      });
    };

    // Every added slot goes into the job, whatever its check result: the worker re-validates each one
    // and a slot that cannot be booked becomes an error row on the job detail.
    const saveAsJob = () => {
      setSaving(true);
      createReservationMultiDuplicationJob(
        {
          sourceReservationId: timeslotIdForDuplication,
          template: buildReservationTemplate({
            customerId: reservationData.customerId,
            locationId: replacementLocation?.locationId ?? reservationData.locationId,
            rampId: isReplaced ? targetRampId : reservationData.rampId,
            order: reservationData.orders[0],
            orders: reservationData.orders,
            carPlate: reservationData.carPlate,
            driverInfo: reservationData.driverInfo,
            carrierNote: reservationData.carrierNote,
            carrierId: reservationData.carrierId,
            reservationName: reservationData.reservationName,
            reservationNote: reservationData.reservationNote,
            reservationActionTypes: reservationData.reservationActionTypes,
            flags: toEditableFlags(reservationData.flags),
            sendNotification,
          }) as ReservationDataInDTO,
          slots: newSlots.map(slot => ({
            since: apiPragueFromDayjsInTz(slot.since, slot.timezoneId),
            till: apiPragueFromDayjsInTz(slot.till, slot.timezoneId),
            utcStart: apiUtcFromDayjs(slot.since),
            utcEnd: apiUtcFromDayjs(slot.till),
            timezoneId: slot.timezoneId,
          })),
          sendNotification,
        },
        d => {
          setTimeslotIdForDuplication(null);
          onComplete();
          navigation.navigate(navigation.urlFunctions.createJobDetail(d.jobId));
        },
        null,
        {
          onError: () => setSaving(false),
          onValidationFailed: () => setSaving(false),
        },
      );
    };

    const save = () => {
      if (asyncJob) {
        saveAsJob();
        return;
      }
      setSaving(true);
      newSlots.forEach(slot => {
        if (slot.state == 'free') {
          changeSlotState({
            ...slot,
            state: 'save',
          });
        }
      });
    };

    const saved = !asyncJob && saving && !newSlots.some(s => s.state === 'check' || s.state === 'free' || s.state === 'save');
    if (reservationData == null) return <></>;

    return (
      <>
        <RingilDialog
          visible={isNotNullOrUndefined(timeslotIdForDuplication)}
          onHide={() => setTimeslotIdForDuplication(null)}
          onSave={() => {
            save();
          }}
          headerText={tr('DuplicateTimeslot.header', 'Duplicate timeslot')}
          wide={true}
          saveButtonDisabled={waitingForRamp || (asyncJob ? saving || newSlots.length === 0 : !newSlots.some(t => t.state === 'free'))}
          showButtonBar={!saved}
        >
          <Row>
            <HorizontalFieldValue
              label={tr(`duplicate.localityAndRamp`, 'Locality and ramp')}
              value={
                isReplaced
                  ? [replacementLocation.locationName, targetRamps.find(r => r.rampId === targetRampId)?.name].filter(t => t != null).join(', ')
                  : [reservationData.locationName, reservationData.rampName].filter(t => t != null).join(', ')
              }
            />
            <HorizontalFieldValue
              label={tr(`duplicate.resevationDateAndTime`, `Resevation date and time`)}
              value={stringFromTimeslotResponse(reservationData.slot) ?? stringFromTimeslotResponse(reservationData.timeslot as any)}
            />
          </Row>

          {isReplaced && (
            <div className={'mt-2 flex flex-col gap-2'}>
              <div className={'text-sm text-complementary-warning'} data-testid={'duplicate-supersededInfo'}>
                {tr('duplicate.locationSuperseded', 'Location {{oldName}} was replaced by {{newName}}. Copies are created on the new location — choose a ramp there.', {
                  oldName: reservationData.locationName,
                  newName: replacementLocation.locationName,
                })}
              </div>
              <VerticalFieldAndValue label={tr('duplicate.targetRamp', 'Ramp')} labelMode={'normal'} required={true}>
                <InputCompanyRamp
                  value={targetRampId}
                  onChange={v => setTargetRampId(v)}
                  companyLocationId={replacementLocation.locationId}
                  companyId={customerId}
                  onRampsLoaded={ramps => {
                    setTargetRamps(ramps);
                    // A ramp of the same name on the replacement is the closest thing to the original one;
                    // anything else is the user's choice to make.
                    const sameName = ramps.find(r => r.name === reservationData.rampName);
                    if (sameName) setTargetRampId(current => current ?? sameName.rampId);
                  }}
                  dataTestId={'duplicate-targetRamp'}
                />
              </VerticalFieldAndValue>
            </div>
          )}

          <Grid>
            {newSlots.map(slot => {
              return (
                <DateAndMessage
                  order={reservationData.orders[0]}
                  orders={reservationData.orders}
                  customerId={reservationData.customerId}
                  locationId={replacementLocation?.locationId ?? reservationData.locationId}
                  carPlate={reservationData.carPlate}
                  driverInfo={reservationData.driverInfo}
                  carrierNote={reservationData.carrierNote}
                  carrierId={reservationData.carrierId}
                  rampId={isReplaced ? targetRampId : reservationData.rampId}
                  waitingForRamp={waitingForRamp}
                  key={slot.id}
                  since={slot.since}
                  till={slot.till}
                  timezoneId={slot.timezoneId}
                  onRemove={() => {
                    removeSlot(slot.id);
                  }}
                  event={slot.state}
                  onChangedState={newState => {
                    changeSlotState({ ...slot, state: newState });
                  }}
                  reservationNote={reservationData.reservationNote}
                  reservationName={reservationData.reservationName}
                  reservationActionTypes={reservationData.reservationActionTypes}
                  flags={reservationData.flags}
                  sendNotification={sendNotification}
                  onComplete={onComplete}
                />
              );
            })}
          </Grid>
          {!saved && (
            <>
              <DatesGrid>
                <DuplicateButtons createNewSlot={createNewSlot} createNewSlotWithDate={createNewSlotWithDate} createNewSlotNext={createNewSlotNext} />
              </DatesGrid>
              <InputCheckbox
                label={tr('duplicate.sendNotification', 'Send notification email')}
                value={sendNotification}
                onChange={(_, v) => setSendNotification(v)}
              />
            </>
          )}

          {saved && (
            <CenteredButtonRow>
              <Button
                label={tr(`duplicate.okClose`, `OK, close`)}
                onClick={() => {
                  setTimeslotIdForDuplication(null);
                  onComplete();
                }}
              />
            </CenteredButtonRow>
          )}
        </RingilDialog>
      </>
    );
  };

  interface DateAndMessageProps {
    since: Dayjs;
    till: Dayjs;
    timezoneId: string;
    onRemove: () => void;
    event: SlotState;
    customerId: string;
    locationId: string;
    order: OrderReservationInDTO;
    orders: GlobalOrderResponse[];
    rampId: string;
    // Copies go to a replacement location whose ramp is not picked yet — nothing may be checked or saved.
    waitingForRamp?: boolean;
    onChangedState: (newState: SlotState) => void;
    carPlate?: string;
    driverInfo?: ReservationDriverInfoOutDTO;
    carrierNote?: string;
    carrierId?: string;
    reservationName?: string;
    reservationNote?: string;
    reservationActionTypes?: ReservationActionTypesEnum[]
    flags?: ReservationFlagDTO[];
    sendNotification: boolean;
    onComplete: () => void;
  }

  interface Message {
    message: any;
    messageType: 'error' | 'info' | 'success' | 'warn';
  }

  const DateAndMessage: React.FC<DateAndMessageProps> = ({
    since,
    till,
    timezoneId,
    onRemove,
    event,
    locationId,
    customerId,
    order,
    rampId,
    waitingForRamp = false,
    onChangedState,
    carPlate,
    carrierNote,
    driverInfo,
    carrierId,
    orders,
    reservationName,
    reservationNote,
    reservationActionTypes,
                                                           flags,
    sendNotification,
    onComplete,
  }) => {
    const { createReservation, checkReservation } = useContext(ApiContext);
    const { tr } = useTranslationLgs();
    const [apiRequest, setApiRequest] = useState(false);
    const [message, setMessage] = useState<Message>();

    const navigation = useNavigation();

    useEffect(() => {
      const c = new AbortController();
      let checkTimeout: ReturnType<typeof setTimeout>;
      if (waitingForRamp) {
        setMessage({
          message: tr('duplicate.selectRampFirst', 'Choose a ramp on the new location first'),
          messageType: 'info',
        });
        return () => {
          c.abort();
        };
      }
      if (event == 'check') {
        checkTimeout = setTimeout(() => {
          setApiRequest(true);
          setMessage({
            message: tr('duplicate.checking', 'Checking...'),
            messageType: 'info',
          });
          checkReservation(
            [],
            {
              customerId,
              locationId,
              timeslot: {
                since: apiPragueFromDayjsInTz(since, timezoneId),
                till: apiPragueFromDayjsInTz(till, timezoneId),
                utcStart: apiUtcFromDayjs(since),
                utcEnd: apiUtcFromDayjs(till),
                timezoneId,
              },
              labelIds: [],
              rampId,
              order,
              openGate: false,
              createExpedition: false,
              flags: flags ?? [],
              sendNotification: true,
            },
            () => {
              setApiRequest(false);
              setMessage({
                message: tr('duplicate.freeTimeslotDetected', 'Free timeslot detected...'),
                messageType: 'success',
              });
              onChangedState('free');
            },
            null,
            {
              onError: e => {
                setApiRequest(false);
                setMessage({
                  message: tr('duplicate.reservationNotEligible', 'Reservation not eligible'),
                  messageType: 'error',
                });
                onChangedState('error');
              },
              onValidationFailed: v => {
                setApiRequest(false);
                setMessage({
                  message: tr('duplicate.reservationNotEligible', 'Reservation not eligible'),
                  messageType: 'error',
                });
                onChangedState('error');
              },
            },
          );
        }, 600);
      }
      if (event == 'save') {
        // save
        setApiRequest(true);
        setMessage({
          message: tr('duplicate.saving', 'Saving...'),
          messageType: 'info',
        });
        createReservation(
          {
            ...buildReservationTemplate({
              customerId,
              locationId,
              rampId,
              order,
              orders,
              carPlate,
              driverInfo,
              carrierNote,
              carrierId,
              reservationName,
              reservationNote,
              reservationActionTypes,
              flags,
              sendNotification,
            }),
            timeslot: {
              since: apiPragueFromDayjsInTz(since, timezoneId),
              till: apiPragueFromDayjsInTz(till, timezoneId),
              utcStart: apiUtcFromDayjs(since),
              utcEnd: apiUtcFromDayjs(till),
              timezoneId,
            },
          },
          d => {
            setApiRequest(false);
            setMessage({
              message: (
                <>
                  <div>
                    {tr('duplicate.reservationCreated', 'Reservation created')} - {/*<a href={`/timeslots/${d.id}`} style={{ fontWeight: 400 }}>*/}
                    <Link
                      to={navigation.createNavigationLink(navigation.urlFunctions.createReservationDetail(d.id))}
                      onClick={() => setTimeslotIdForDuplication(null)}
                    >
                      {d.applicationId}
                    </Link>
                  </div>
                </>
              ),
              messageType: 'info',
            });
            onChangedState('saved');
            onComplete();
          },
          null,
          {
            onError: e => {
              setApiRequest(false);
              setMessage({
                message: tr('duplicate.reservationNotEligible', 'Reservation not eligible'),
                messageType: 'error',
              });
              onChangedState('error');
            },
            onValidationFailed: v => {
              setApiRequest(false);
              setMessage({
                message: tr('duplicate.reservationNotEligible', 'Reservation not eligible'),
                messageType: 'error',
              });
              onChangedState('error');
            },
          },
        );
      }
      return () => {
        clearTimeout(checkTimeout);
        c.abort();
      };
    }, [event, waitingForRamp]);

    return (
      <>
        <div>
          <RowWithSpaceBetween>
            <div>{formatDateFromDayjsWithLocale(since)}</div>
            <RowWithGap>
              <RowNoGap>
                {stringHHmmFromDayjs(since)} - {stringHHmmFromDayjs(till)}
              </RowNoGap>
              <Clickable onClick={() => onRemove()}>
                <BlueIcon className={'pi pi-trash'} />
              </Clickable>
            </RowWithGap>
          </RowWithSpaceBetween>
        </div>
        <div>
          {apiRequest && (
            <>
              <ProgressSpinner style={{ width: '15px', height: '15px', marginRight: '0.5rem' }} strokeWidth="3" fill="var(--surface-ground)" animationDuration="1s" />
            </>
          )}
          {message && message.messageType == 'info' && <span>{message.message}</span>}
          {message && message.messageType == 'success' && <span style={{ color: 'var(--ringil-success-color)' }}>{message.message}</span>}
          {message && message.messageType == 'error' && <span style={{ color: 'var(--ringil-error-color)' }}>{message.message}</span>}
        </div>
      </>
    );
  };

  const DuplicateButtons = ({
    // @ts-ignore
    createNewSlot,
    // @ts-ignore
    createNewSlotWithDate,
    // @ts-ignore
    createNewSlotNext,
  }) => {
    const [customDate, setCustomDate] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(null);
    const { tr } = useTranslationLgs();
    const { i18n } = useTranslation();
    const { shiftOptions, weekDayOptions } = useDuplicateDateOptions();

    return (
      <>
        <div>
          {customDate && (
            <CustomRow>
              <Calendar
                dateFormat={shortDateFormat}
                style={{ width: '140px' }}
                locale={i18n.language}
                showButtonBar={true}
                showIcon
                value={selectedDate}
                onChange={e => setSelectedDate(e.value as Date)}
              />
              <Button
                label={tr(`duplicate.save`, `Save`)}
                onClick={() => {
                  createNewSlotWithDate(dayjs(selectedDate));
                  setCustomDate(false);
                  setSelectedDate(null);
                }}
              />
              <BlueIcon
                className={'pi pi-trash'}
                onClick={() => {
                  setCustomDate(false);
                }}
              />
            </CustomRow>
          )}
        </div>
        <>
          {!customDate && (
            <div className={'flex flex-col gap-2 min-w-0'}>
              <div className={'flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0'}>
                {shiftOptions.map(o => (
                  <span key={o.days} className={'inline-flex items-center gap-2 whitespace-nowrap'}>
                    <Clickable onClick={() => createNewSlot(o.days)}>{o.label}</Clickable>
                    <span className={'text-content-tertiary'}>|</span>
                  </span>
                ))}
                <Clickable onClick={() => setCustomDate(true)}>{tr(`duplicate.vlastni`, `Vlastni`)}</Clickable>
              </div>
              <div className={'flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0'}>
                {weekDayOptions.map((o, index) => (
                  <span key={o.day} className={'inline-flex items-center gap-2 whitespace-nowrap'}>
                    <Clickable onClick={() => createNewSlotNext(o.day)}>{o.label}</Clickable>
                    {index < weekDayOptions.length - 1 && <span className={'text-content-tertiary'}>|</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      </>
    );
  };

  return { setTimeslotIdForDuplication, DuplicateTimeslot };
};

const CustomRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 0.5rem;
`;
const RowNoGap = styled.div`
  display: flex;
  flex-wrap: nowrap;
  white-space: nowrap;
`;

const Grid = styled.div`
  margin-top: 1rem;
  display: grid;
  grid-template-columns: 1fr 2fr;
`;

const DatesGrid = styled.div`
  margin-top: 1rem;
  display: grid;
  grid-template-columns: 1fr 2fr;
`;
